import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../lib/firebase'
import type { Raffle, Ticket } from '../types'

interface RaffleState {
  raffles: Raffle[]
  activeRaffle: Raffle | null
  raffleTickets: Ticket[]
  selectedTickets: number[]
  isLoading: boolean
  error: string | null
  unsubscribeRaffles: (() => void) | null
  unsubscribeActiveRaffle: (() => void) | null
  unsubscribeTickets: (() => void) | null
}

interface RaffleActions {
  // Raffle management
  fetchRaffles: () => Promise<void>
  fetchAllRaffles: () => Promise<void>
  subscribeToRaffles: () => void
  subscribeToAllRaffles: () => void
  fetchRaffleById: (id: string) => Promise<void>
  subscribeToRaffle: (id: string) => void
  createRaffle: (raffle: Omit<Raffle, 'id' | 'createdAt'>) => Promise<string>
  updateRaffle: (id: string, updates: Partial<Raffle>) => Promise<void>
  deleteRaffle: (id: string) => Promise<void>
  
  // Ticket management
  fetchRaffleTickets: (raffleId: string) => Promise<void>
  subscribeToRaffleTickets: (raffleId: string) => void
  selectTicket: (number: number) => void
  deselectTicket: (number: number) => void
  clearSelection: () => void
  purchaseTickets: (raffleId: string, tickets: number[]) => Promise<void>
  
  // State management
  setActiveRaffle: (raffle: Raffle | null) => void
  setError: (error: string | null) => void
  clearError: () => void
  cleanup: () => void
}

export type RaffleStore = RaffleState & RaffleActions

export const useRaffleStore = create<RaffleStore>()(
  devtools(
    (set, get) => ({
      // State
      raffles: [],
      activeRaffle: null,
      raffleTickets: [],
      selectedTickets: [],
      isLoading: false,
      error: null,
      unsubscribeRaffles: null,
      unsubscribeActiveRaffle: null,
      unsubscribeTickets: null,

      // Actions
      setError: (error) => {
        set({ error, isLoading: false })
      },

      clearError: () => {
        set({ error: null })
      },

      fetchRaffles: async () => {
        try {
          set({ isLoading: true, error: null })
          
          const q = query(
            collection(db, 'raffles'),
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc')
          )
          
          const querySnapshot = await getDocs(q)
          const raffles: Raffle[] = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            publishedAt: doc.data().publishedAt?.toDate(),
            completedAt: doc.data().completedAt?.toDate()
          })) as Raffle[]
          
          set({ raffles, isLoading: false })
        } catch (error: unknown) {
          console.error('Error fetching raffles:', error)
          set({ 
            error: 'Error al cargar los sorteos',
            isLoading: false
          })
        }
      },

      fetchAllRaffles: async () => {
        try {
          set({ isLoading: true, error: null })
          
          const q = query(
            collection(db, 'raffles'),
            orderBy('createdAt', 'desc')
          )
          
          const querySnapshot = await getDocs(q)
          const raffles: Raffle[] = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            publishedAt: doc.data().publishedAt?.toDate(),
            completedAt: doc.data().completedAt?.toDate()
          })) as Raffle[]
          
          set({ raffles, isLoading: false })
        } catch (error: unknown) {
          console.error('Error fetching all raffles:', error)
          set({ 
            error: 'Error al cargar los sorteos',
            isLoading: false
          })
        }
      },

      subscribeToRaffles: () => {
        try {
          set({ isLoading: true, error: null })
          
          const q = query(
            collection(db, 'raffles'),
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc')
          )
          
          const unsubscribe = onSnapshot(q, (snapshot) => {
            const raffles: Raffle[] = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date(),
              publishedAt: doc.data().publishedAt?.toDate(),
              completedAt: doc.data().completedAt?.toDate()
            })) as Raffle[]
            
            // Always set loading to false once we get a response, even if empty
            set({ raffles, isLoading: false })
          }, (error) => {
            console.error('Error in raffles subscription:', error)
            set({ error: 'Error al cargar los sorteos en tiempo real', isLoading: false })
          })
          
          set({ unsubscribeRaffles: unsubscribe })
        } catch (error) {
          console.error('Error setting up raffles subscription:', error)
          set({ 
            error: 'Error al conectar con la base de datos',
            isLoading: false 
          })
        }
      },

      subscribeToAllRaffles: () => {
        try {
          set({ isLoading: true, error: null })
          
          const q = query(
            collection(db, 'raffles'),
            orderBy('createdAt', 'desc')
          )
          
          const unsubscribe = onSnapshot(q, (snapshot) => {
            const raffles: Raffle[] = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date(),
              publishedAt: doc.data().publishedAt?.toDate(),
              completedAt: doc.data().completedAt?.toDate()
            })) as Raffle[]
            
            set({ raffles, isLoading: false })
          }, (error) => {
            console.error('Error in all raffles subscription:', error)
            set({ error: 'Error al cargar los sorteos en tiempo real', isLoading: false })
          })
          
          set({ unsubscribeRaffles: unsubscribe })
        } catch (error) {
          console.error('Error setting up all raffles subscription:', error)
          set({ isLoading: false })
        }
      },

      fetchRaffleById: async (id: string) => {
        try {
          set({ isLoading: true, error: null })

          const docRef = doc(db, 'raffles', id)
          const docSnap = await getDoc(docRef)

          if (docSnap.exists()) {
            const raffleData = {
              id: docSnap.id,
              ...docSnap.data(),
              createdAt: docSnap.data().createdAt?.toDate() || new Date(),
              publishedAt: docSnap.data().publishedAt?.toDate(),
              completedAt: docSnap.data().completedAt?.toDate()
            } as Raffle

            set({ activeRaffle: raffleData, isLoading: false })

            // FIXED: Don't auto-subscribe here - let subscribeToRaffle handle it properly
          } else {
            set({
              error: 'Sorteo no encontrado',
              isLoading: false
            })
          }
        } catch (error: unknown) {
          console.error('Error fetching raffle:', error)
          set({
            error: 'Error al cargar el sorteo',
            isLoading: false
          })
        }
      },

      subscribeToRaffle: (id: string) => {
        try {
          // CACHE FIX: Clean up any existing subscriptions first
          const state = get()
          if (state.unsubscribeActiveRaffle) {
            state.unsubscribeActiveRaffle()
            set({ unsubscribeActiveRaffle: null })
          }
          if (state.unsubscribeTickets) {
            state.unsubscribeTickets()
            set({ unsubscribeTickets: null })
          }

          // CACHE FIX: Clear any stale data from previous raffle
          set({
            activeRaffle: null,
            raffleTickets: [],
            selectedTickets: [], // Clear selected tickets from previous raffle
            error: null
          })

          const docRef = doc(db, 'raffles', id)

          const unsubscribeRaffle = onSnapshot(docRef, (doc) => {
            if (doc.exists()) {
              const raffleData = {
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date(),
                publishedAt: doc.data().publishedAt?.toDate(),
                completedAt: doc.data().completedAt?.toDate()
              } as Raffle

              set({ activeRaffle: raffleData })
            } else {
              set({
                activeRaffle: null,
                error: 'Sorteo no encontrado'
              })
            }
          }, (error) => {
            console.error('Error in raffle subscription:', error)
            set({ error: 'Error al cargar el sorteo en tiempo real' })
          })

          // Also subscribe to tickets in real time
          const ticketsQuery = query(
            collection(db, 'tickets'),
            where('raffleId', '==', id),
            orderBy('number', 'asc')
          )

          const unsubscribeTickets = onSnapshot(ticketsQuery, (querySnapshot) => {
            const tickets: Ticket[] = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              purchasedAt: doc.data().purchasedAt?.toDate(),
              reservedAt: doc.data().reservedAt?.toDate(),
              reservationExpiresAt: doc.data().reservationExpiresAt?.toDate()
            })) as Ticket[]

            // CACHE FIX: Always update tickets to ensure fresh data
            set({ raffleTickets: tickets })
          }, (error) => {
            console.error('Error in tickets subscription:', error)
          })

          // Store both unsubscribe functions
          set({
            unsubscribeActiveRaffle: () => {
              unsubscribeRaffle()
              unsubscribeTickets()
            },
            unsubscribeTickets: unsubscribeTickets
          })
        } catch (error) {
          console.error('Error setting up raffle subscription:', error)
        }
      },

      fetchRaffleTickets: async (raffleId: string) => {
        try {
          const q = query(
            collection(db, 'tickets'),
            where('raffleId', '==', raffleId),
            orderBy('number', 'asc')
          )

          const querySnapshot = await getDocs(q)
          const tickets: Ticket[] = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            purchasedAt: doc.data().purchasedAt?.toDate() || new Date()
          })) as Ticket[]

          set({ raffleTickets: tickets })
        } catch (error: unknown) {
          console.error('Error fetching tickets:', error)
          set({ error: 'Error al cargar los números vendidos' })
        }
      },

      subscribeToRaffleTickets: (raffleId: string) => {
        const state = get()

        // Cleanup existing subscription
        if (state.unsubscribeTickets) {
          state.unsubscribeTickets()
        }

        try {
          const q = query(
            collection(db, 'tickets'),
            where('raffleId', '==', raffleId),
            orderBy('number', 'asc')
          )

          const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const tickets: Ticket[] = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              purchasedAt: doc.data().purchasedAt?.toDate() || new Date()
            })) as Ticket[]

            // Real-time update received
            set({ raffleTickets: tickets })
          }, (error) => {
            console.error('Error in tickets subscription:', error)
            set({ error: 'Error al cargar los números en tiempo real' })
          })

          set({ unsubscribeTickets: unsubscribe })
        } catch (error: unknown) {
          console.error('Error setting up tickets subscription:', error)
          set({ error: 'Error al configurar actualización en tiempo real' })
        }
      },

      createRaffle: async (raffleData) => {
        try {
          set({ isLoading: true, error: null })
          
          const docRef = await addDoc(collection(db, 'raffles'), {
            ...raffleData,
            createdAt: serverTimestamp(),
            maxTickets: 100,
            totalTickets: 100
          })
          
          set({ isLoading: false })
          return docRef.id
        } catch (error: unknown) {
          console.error('Error creating raffle:', error)
          set({ 
            error: 'Error al crear el sorteo',
            isLoading: false
          })
          throw error
        }
      },

      updateRaffle: async (id: string, updates: Partial<Raffle>) => {
        try {
          set({ isLoading: true, error: null })
          
          const docRef = doc(db, 'raffles', id)
          await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp()
          })
          
          set({ isLoading: false })
        } catch (error: unknown) {
          console.error('Error updating raffle:', error)
          set({ 
            error: 'Error al actualizar el sorteo',
            isLoading: false
          })
          throw error
        }
      },

      deleteRaffle: async (id: string) => {
        try {
          set({ isLoading: true, error: null })
          
          await deleteDoc(doc(db, 'raffles', id))
          
          // Remove from local state
          set((state) => ({
            raffles: state.raffles.filter(r => r.id !== id),
            activeRaffle: state.activeRaffle?.id === id ? null : state.activeRaffle,
            isLoading: false
          }))
        } catch (error: unknown) {
          console.error('Error deleting raffle:', error)
          set({ 
            error: 'Error al eliminar el sorteo',
            isLoading: false
          })
          throw error
        }
      },

      selectTicket: (number: number) => {
        set((state) => ({
          selectedTickets: [...state.selectedTickets, number]
        }))
      },

      deselectTicket: (number: number) => {
        set((state) => ({
          selectedTickets: state.selectedTickets.filter(n => n !== number)
        }))
      },

      clearSelection: () => {
        set({ selectedTickets: [] })
      },

      purchaseTickets: async (raffleId: string, ticketNumbers: number[]) => {
        try {
          set({ isLoading: true, error: null })
          
          // Create tickets in Firestore
          const ticketPromises = ticketNumbers.map(async (number) => {
            const ticketData = {
              raffleId,
              number,
              userId: 'current-user-id', // TODO: Get from auth store
              purchasePrice: 0, // TODO: Get from raffle basePrice
              purchasedAt: serverTimestamp()
            }
            
            return addDoc(collection(db, 'tickets'), ticketData)
          })
          
          // Wait for all tickets to be created
          await Promise.all(ticketPromises)
          
          // No need to update raffle statistics - they are calculated in real-time from orders and tickets
          
          // Clear selection and refresh data
          set({ selectedTickets: [], isLoading: false })
          
          // Real-time listener will automatically update tickets
          
        } catch (error: unknown) {
          console.error('Error purchasing tickets:', error)
          set({ 
            error: 'Error al comprar los números. Inténtalo de nuevo.',
            isLoading: false
          })
          throw error
        }
      },

      setActiveRaffle: (raffle: Raffle | null) => {
        set({ activeRaffle: raffle })
      },

      cleanup: () => {
        const state = get()

        // CACHE FIX: Clean up all subscriptions and reset state
        if (state.unsubscribeRaffles) {
          state.unsubscribeRaffles()
        }

        if (state.unsubscribeActiveRaffle) {
          state.unsubscribeActiveRaffle()
        }

        if (state.unsubscribeTickets) {
          state.unsubscribeTickets()
        }

        // CACHE FIX: Reset all state to prevent stale data
        set({
          unsubscribeRaffles: null,
          unsubscribeActiveRaffle: null,
          unsubscribeTickets: null,
          activeRaffle: null,
          raffleTickets: [],
          selectedTickets: [],
          error: null
        })
      }
    }),
    {
      name: 'raffle-store'
    }
  )
)